import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscriptionAPI, paymentAPI } from '../services/api';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import Footer from './Footer';
import { trackSubscription, trackError } from '../utils/analytics';
import { captureAndStoreUTM } from '../utils/utmCapture';
import {
  CheckIcon,
  StarIcon,
  TrophyIcon,
  SparklesIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';

// Fallback data will use translations - this is just for structure
const FALLBACK_PLANS = [
  {
    tier: 'free',
    features: [] // Will be populated from translations
  },
  {
    tier: 'premium',
    features: []
  },
  {
    tier: 'pro',
    features: []
  },
  {
    tier: 'agency',
    features: []
  }
];

const Pricing = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly'); // monthly or annual
  
  // Track pricing page view
  useEffect(() => {
    // Capture UTM parameters on page load
    captureAndStoreUTM();
    // Track pricing page view
    trackSubscription.viewPricing();
  }, []);

  // Fetch payment configuration (provider and product IDs)
  const { data: paymentConfig } = useQuery({
    queryKey: ['payment-config'],
    queryFn: () => paymentAPI.getConfig().then(res => res.data),
    retry: 2,
    retryDelay: 1000,
  });

  // Fetch subscription plans with fallback
  const { data: apiPlans, isLoading, error } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => subscriptionAPI.getPlans().then(res => res.data),
    retry: 2,
    retryDelay: 1000,
  });

  // Use API data or fallback data
  const rawPlans = apiPlans && apiPlans.length > 0 ? apiPlans : FALLBACK_PLANS;
  
  // Sort plans in the desired order: free, premium, pro, agency
  const plans = [...rawPlans].sort((a, b) => {
    const order = { 'free': 0, 'premium': 1, 'pro': 2, 'agency': 3 };
    // Normalize tier to lowercase string in case it comes as enum or different case
    const tierA = (a.tier || '').toLowerCase();
    const tierB = (b.tier || '').toLowerCase();
    return (order[tierA] ?? 999) - (order[tierB] ?? 999);
  });

  // Fetch current subscription
  const { data: subscription } = useQuery({
    queryKey: ['current-subscription'],
    queryFn: () => subscriptionAPI.getCurrentSubscription().then(res => res.data),
    enabled: !!user,
    retry: 1,
  });

  const handleSubscribe = async (tier) => {
    if (!user) {
      // Show friendly message and redirect to register
      const planName = t(`pricing.plans.${tier}.name`);
      toast.info(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">{t('pricing.signupRequired.title')}</span>
          <span className="text-sm">{t('pricing.signupRequired.message', { plan: planName })}</span>
        </div>,
        {
          autoClose: 4000,
          icon: '✨'
        }
      );
      // Small delay so user can see the message before redirect
      setTimeout(() => {
        navigate('/register');
      }, 1500);
      return;
    }

    if (tier === 'free') {
      toast.info(t('pricing.getStartedFree'));
      navigate('/register');
      return;
    }

    setLoading(tier);
    
    // Get plan details for tracking
    const plan = plans.find(p => p.tier === tier);
    const price = billingPeriod === 'annual' 
      ? (plan?.pricing?.annual || 0) 
      : (plan?.pricing?.monthly || 0);
    
    // Track plan selection
    trackSubscription.selectPlan(tier, price);
    
    try {
      // Get product ID from payment config
      if (!paymentConfig || !paymentConfig.products) {
        toast.error('Payment configuration not loaded. Please refresh the page.');
        return;
      }

      // Find the product for this tier (case-insensitive comparison)
      const product = paymentConfig.products.find(p => p.tier.toLowerCase() === tier.toLowerCase());
      if (!product) {
        toast.error(`Product not configured for ${tier} tier`);
        console.error('Product not found for tier:', tier, 'Available products:', paymentConfig.products);
        return;
      }

      // Select product ID based on billing period
      const productId = billingPeriod === 'annual' 
        ? product.annual_product_id 
        : product.monthly_product_id;
      
      if (!productId || productId.trim() === '') {
        toast.error(`${billingPeriod === 'annual' ? 'Annual' : 'Monthly'} plan not configured for ${tier} tier`);
        console.error('Product ID missing for tier:', tier, 'Billing period:', billingPeriod, 'Product:', product);
        return;
      }

      console.log('Creating checkout session with:', { tier, productId, billingPeriod });
      
      const response = await subscriptionAPI.createCheckoutSession({
        price_id: productId,
        success_url: `${window.location.origin}/dashboard?success=true`,
        cancel_url: `${window.location.origin}/pricing?canceled=true`
      });

      console.log('Checkout session response:', response);

      if (!response || !response.data || !response.data.checkout_url) {
        console.error('Invalid checkout response:', response);
        toast.error('Failed to get checkout URL. Please try again.');
        return;
      }

      // Track checkout started with item details for GA4
      const planName = `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan`;
      trackSubscription.startCheckout(planName, price, productId);
      
      console.log('Redirecting to checkout URL:', response.data.checkout_url);
      
      // Redirect to checkout (works for both Stripe and Polar)
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to start checkout';
      // Track error
      trackError.paymentError('checkout_start_failed', errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(null);
    }
  };

  const getPlanIcon = (tier) => {
    switch (tier) {
      case 'free':
        return <StarIcon className="h-8 w-8 text-blue-400" />;
      case 'premium':
        return <SparklesIcon className="h-8 w-8 text-purple-400" />;
      case 'pro':
        return <TrophyIcon className="h-8 w-8 text-yellow-400" />;
      case 'agency':
        return <BuildingOfficeIcon className="h-8 w-8 text-red-400" />;
      default:
        return <StarIcon className="h-8 w-8 text-blue-400" />;
    }
  };

  const getPlanPrice = (tier) => {
    // Use dynamic pricing from payment config if available
    if (paymentConfig && paymentConfig.products) {
      const product = paymentConfig.products.find(p => p.tier === tier);
      if (product) {
        if (billingPeriod === 'annual' && product.annual_price) {
          return `$${product.annual_price.toFixed(2)}`;
        } else if (product.monthly_price) {
          return `$${product.monthly_price.toFixed(2)}`;
        }
      }
    }
    
    // Fallback to default prices
    const monthlyPrices = {
      free: '$0',
      premium: '$9.99',
      pro: '$24.99',
      agency: '$49.99'
    };
    
    const annualPrices = {
      free: '$0',
      premium: '$79.99',    // ~17% discount
      pro: '$249.99',       // ~17% discount
      agency: '$499.99'     // ~17% discount
    };
    
    return billingPeriod === 'monthly' ? monthlyPrices[tier] : annualPrices[tier];
  };

  const getPricePeriod = () => {
    return billingPeriod === 'monthly' ? '/month' : '/year';
  };

  const getAnnualSavings = (tier) => {
    if (billingPeriod === 'annual' && tier !== 'free') {
      const monthlyRates = {
        premium: 9.99,
        pro: 24.99,
        agency: 54.99
      };
      const annualRates = {
        premium: 99.99,
        pro: 249.99,
        agency: 549.99
      };
      const monthlyCost = monthlyRates[tier] * 12;
      const annualCost = annualRates[tier];
      const savings = Math.round(((monthlyCost - annualCost) / monthlyCost) * 100);
      return `Save ${savings}%`;
    }
    return billingPeriod === 'monthly' && tier !== 'free' ? 'Billed monthly' : null;
  };

  const getPlanName = (tier) => {
    return t(`pricing.plans.${tier}.name`);
  };

  const isPlanCurrent = (tier) => {
    // Only show current plan if user is logged in
    if (!user) {
      return false;
    }
    // Default to 'free' if no subscription data but user is logged in
    const currentTier = subscription?.tier || 'free';
    return currentTier === tier;
  };

  const getPlanButtonText = (tier) => {
    if (isPlanCurrent(tier)) {
      return t('pricing.currentPlanButton');
    }
    if (tier === 'free' && !user) {
      return t('pricing.getStartedFree');
    }
    if (tier === 'free') {
      return t('pricing.switchToFree');
    }
    return t('pricing.upgradeNow');
  };

  // FAQ data
  const faqs = [
    {
      question: t('pricing.faq.cancelAnytime.question'),
      answer: t('pricing.faq.cancelAnytime.answer')
    },
    {
      question: t('pricing.faq.dataSecure.question'),
      answer: t('pricing.faq.dataSecure.answer')
    },
    {
      question: t('pricing.faq.downgrade.question'),
      answer: t('pricing.faq.downgrade.answer')
    },
    {
      question: t('pricing.faq.refunds.question'),
      answer: t('pricing.faq.refunds.answer')
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <Navbar />
      
      <div className="relative pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
              {t('pricing.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              {t('pricing.subtitle')}
            </p>
            
            {/* Billing Period Toggle */}
            <div className="mt-6 sm:mt-8 flex justify-center">
              <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-1.5 sm:p-2 border border-white/20 shadow-2xl">
                <div className="flex items-center">
                  <button
                    onClick={() => setBillingPeriod('monthly')}
                    className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                      billingPeriod === 'monthly'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {t('pricing.monthly')}
                  </button>
                  <button
                    onClick={() => setBillingPeriod('annual')}
                    className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 flex items-center ${
                      billingPeriod === 'annual'
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {t('pricing.annual')}
                    <span className="ml-1.5 sm:ml-2 inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800">
                      {t('pricing.save')}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Current Subscription Status */}
          {user && (
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 mb-6 sm:mb-8 border border-white/20 shadow-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">{t('pricing.currentPlan')}</h3>
                  <p className="text-sm sm:text-base text-gray-300">
                    {t('pricing.youAreOn', { tier: t(`pricing.plans.${subscription?.tier || 'free'}.name`) })}
                  </p>
                </div>
                <div className="sm:text-right">
                  <span className="inline-flex items-center px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {subscription?.status || t('pricing.active')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Debug info for development */}
          {error && process.env.NODE_ENV === 'development' && (
            <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-yellow-200 text-sm">
              API Error: Using fallback data. {error.message}
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-7xl mx-auto">
            {plans?.map((plan) => (
              <div
                key={plan.tier}
                className={`relative bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 border shadow-2xl transition-all duration-300 hover:scale-[1.02] sm:hover:scale-105 flex flex-col ${
                  plan.tier === 'premium'
                    ? 'border-purple-400/50 ring-2 ring-purple-400/20'
                    : 'border-white/20 hover:border-white/30'
                }`}
              >
                {/* Popular Badge */}
                {plan.tier === 'premium' && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                      {t('pricing.mostPopular')}
                    </div>
                  </div>
                )}

                {/* Current Plan Badge */}
                {user && isPlanCurrent(plan.tier) && (
                  <div className="absolute -top-4 right-4">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                      {t('pricing.active')}
                    </div>
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-5 sm:mb-8">
                  <div className="flex justify-center mb-3 sm:mb-4">
                    {getPlanIcon(plan.tier)}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 sm:mb-2">
                    {getPlanName(plan.tier)}
                    {user && isPlanCurrent(plan.tier) && (
                      <span className="ml-2 text-xs sm:text-sm text-green-400 font-normal">{t('pricing.current')}</span>
                    )}
                  </h3>
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                    {getPlanPrice(plan.tier)}
                    <span className="text-base sm:text-lg text-gray-300 font-normal">{getPricePeriod()}</span>
                  </div>
                  {plan.tier !== 'free' && (
                    <p className="text-gray-300 text-xs sm:text-sm">{getAnnualSavings(plan.tier)}</p>
                  )}
                </div>

                {/* Features List */}
                <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 flex-grow">
                  {t(`pricing.plans.${plan.tier}.features`, { returnObjects: true }).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <CheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                      <span className="text-gray-200 text-xs sm:text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="mt-auto">
                  <button
                    onClick={() => {
                      if (plan.tier === 'free' && !user) {
                        navigate('/register');
                      } else {
                        handleSubscribe(plan.tier);
                      }
                    }}
                    disabled={loading === plan.tier || (user && isPlanCurrent(plan.tier))}
                    className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-200 ${
                      (user && isPlanCurrent(plan.tier))
                        ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                        : plan.tier === 'premium'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                        : plan.tier === 'pro'
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                        : plan.tier === 'agency'
                        ? 'bg-gradient-to-r from-purple-500 to-gray-500 hover:from-purple-600 hover:to-gray-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30'
                    }`}
                  >
                    {loading === plan.tier ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('pricing.processing')}
                      </div>
                    ) : (
                      getPlanButtonText(plan.tier)
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="mt-10 sm:mt-16">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-white/20 shadow-2xl">
              <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-6 sm:mb-8">
                {t('pricing.faq.title')}
              </h3>
              <div className="space-y-4 sm:space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="border-b border-white/10 pb-4 sm:pb-6 last:border-b-0">
                    <h4 className="text-base sm:text-lg font-semibold text-white mb-1.5 sm:mb-2">
                      {faq.question}
                    </h4>
                    <p className="text-sm sm:text-base text-gray-300">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer variant="dark" />
    </div>
  );
};

export default Pricing; 